import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { RootState, AppDispatch } from '@/store/store'
import {
    FiBriefcase,
    FiDollarSign,
    FiUsers,
    FiStar,
    FiPlus,
    FiMessageSquare,
    FiTrendingUp,
    FiEye,
    FiCheckCircle,
    FiClock,
    FiSearch,
    FiShield,
    FiAlertCircle,
    FiBook,
    FiEdit2,
    FiExternalLink,
    FiTrendingDown,
    FiArrowRight,
    FiRefreshCw,
    FiLock,
    FiAward
} from 'react-icons/fi'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { formatCurrency, formatDate } from '@/utils/formatters'
import { useGetDashboardDataQuery } from '@/services/statsApi'
import VerificationBanner from '@/components/verification/VerificationBanner'
import VerificationProgress from '@/components/verification/VerificationProgress'
import EmailVerificationBanner from '@/components/verification/EmailVerificationBanner'
import ProgressIndicator from '@/components/ui/ProgressIndicator'
import { successStoriesApi } from '@/services/api/successStories'
import { SuccessStory } from '@/types/successStory'
import { useGetCurrentSubscriptionQuery } from '@/services/subscriptionRtkApi'
import { useGetUserAccessLimitsQuery } from '@/services/accessLimitsRtkApi'
import { useGetVerificationStatusQuery } from '@/services/verificationRtkApi'
import { Accordion, AccordionItem } from '@/components/ui/Accordion'
import { BalanceCard } from '@/components/wallet'
import { fetchBalance } from '@/store/slices/walletSlice'

const DashboardPage: React.FC = () => {
    const formatLimitUsage = (current?: number | null, limit?: number | null, unlimited?: boolean) => {
        if (unlimited || limit === 0) {
            return 'Unlimited'
        }
        if (limit === undefined || limit === null) {
            return '--'
        }
        return `${current ?? 0}/${limit}`
    }
    const formatBooleanLabel = (value?: boolean | null, affirmative: string = 'Enabled', negative: string = 'Disabled') => {
        if (value === undefined || value === null) {
            return '--'
        }
        return value ? affirmative : negative
    }
    const formatPercentValue = (value?: number | null) => {
        if (value === undefined || value === null) {
            return '--'
        }
        return `${value}%`
    }
    const formatNumberValue = (value?: number | null) => {
        if (value === undefined || value === null) {
            return '--'
        }
        return value.toLocaleString()
    }
    const formatLabelValue = (value?: string | null) => {
        if (!value) {
            return '--'
        }
        return value
            .split(/[\s_]/)
            .filter(Boolean)
            .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
            .join(' ')
    }

    const { user, isAuthenticated } = useSelector((state: RootState) => state.auth)
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()
    const [successStory, setSuccessStory] = useState<SuccessStory | null>(null)
    const [storyLoading, setStoryLoading] = useState(true)
    const [isRefreshing, setIsRefreshing] = useState(false)

    const {
        data: dashboardData,
        error: dashboardError,
        isLoading: dashboardLoading,
        refetch: refetchDashboard
    } = useGetDashboardDataQuery(undefined, {
        skip: !isAuthenticated
    })

    const {
        data: subscription,
        isLoading: subscriptionLoading,
        refetch: refetchSubscription
    } = useGetCurrentSubscriptionQuery(undefined, {
        skip: !isAuthenticated,
    })

    const {
        data: accessLimits,
        isLoading: accessLimitsLoading,
        error: accessLimitsError,
        refetch: refetchAccessLimits
    } = useGetUserAccessLimitsQuery(undefined, {
        skip: !isAuthenticated,
    })

    const {
        data: verificationStatus,
        refetch: refetchVerification
    } = useGetVerificationStatusQuery(undefined, {
        skip: !isAuthenticated,
    })

    const { balance: walletBalance } = useSelector((state: RootState) => state.wallet)

    useEffect(() => {
        if (isAuthenticated) {
            dispatch(fetchBalance(undefined))
        }
    }, [isAuthenticated, dispatch])



    // Load user's success story
    useEffect(() => {
        const fetchSuccessStory = async () => {
            if (!isAuthenticated) return

            try {
                setStoryLoading(true)
                const response = await successStoriesApi.getUserSuccessStory()
                // @ts-expect-error - API returns nested response structure
                if (response.data?.data?.story) {
                    // @ts-expect-error - API returns nested response structure
                    setSuccessStory(response.data.data.story)
                }
            } catch (error) {
                console.error('Failed to load success story:', error)
            } finally {
                setStoryLoading(false)
            }
        }

        fetchSuccessStory()
    }, [isAuthenticated])



    if (!isAuthenticated) {
        navigate('/login')
        return null
    }

    if (dashboardLoading) {
        return (
            <div className="flex items-center justify-center py-16">
                <LoadingSpinner size="xl" />
            </div>
        )
    }

    if (dashboardError) {
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                        Failed to load dashboard data
                    </h2>
                    <button
                        onClick={refetchDashboard}
                        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        )
    }

    // Extract data from API response
    const stats = dashboardData?.data?.stats || {
        totalGigs: 0,
        activeApplications: 0,
        earnings: 0,
        rating: 0,
        completedJobs: 0,
        profileViews: 0
    }

    const recentActivity = dashboardData?.data?.recentActivity || []
    const dashboardUser = dashboardData?.data?.user

    const isWorker = user?.role === 'worker'
    const overallVerificationStatus = verificationStatus ?? (user?.profile?.verified ? 'verified' : user?.identityVerification?.status) ?? 'unverified'
    const subscriptionTier = subscription?.tier
    const hasActivePaidSubscription = Boolean(
        subscription?.status === 'active' &&
        subscriptionTier &&
        ['pro', 'enterprise'].includes(subscriptionTier)
    )
    const gigUsage = accessLimits
        ? {
            ...accessLimits.gigs,
            unlimited: accessLimits.gigs.unlimited || hasActivePaidSubscription
        }
        : null
    const applicationUsage = accessLimits
        ? {
            ...accessLimits.applications,
            unlimited: accessLimits.applications.unlimited || hasActivePaidSubscription
        }
        : null
    const hasUnlimitedAccess = user?.role === 'employer'
        ? gigUsage?.unlimited
        : applicationUsage?.unlimited
    const isLimitReached = user?.role === 'employer'
        ? accessLimits?.gigs && accessLimits.gigs.current >= accessLimits.gigs.limit
        : accessLimits?.applications && accessLimits.applications.current >= accessLimits.applications.limit
    
    const shouldShowUpgradeBanner = Boolean(
        accessLimits && (
            (!subscription || subscriptionTier === 'free' || subscription?.monthlyAmount === 0 || subscription?.monthlyAmount === undefined)
            || isLimitReached
        )
    )

    const enrichedUser = user ?? dashboardUser ?? null
    const baseUser = (enrichedUser as any) || {}
    const authUser = (user as any) || {}
    const statsUser = (dashboardUser as any) || {}

    const trustSystemDetails = baseUser.trustSystem || authUser.trustSystem || statsUser.trustSystem
    const verificationBenefits = baseUser.identityVerification?.benefits || authUser.identityVerification?.benefits || statsUser.identityVerification?.benefits
    const identityStatus = baseUser.identityVerification?.status || (baseUser.profile?.verified ? 'verified' : undefined) || authUser.identityVerification?.status || statsUser.identityVerification?.status
    const bankVerified = baseUser.bankDetails?.isVerified ?? authUser.bankDetails?.isVerified ?? statsUser.bankDetails?.isVerified
    const twoFactorEnabled = baseUser.twoFactorEnabled ?? authUser.twoFactorEnabled ?? statsUser.twoFactorEnabled
    const communicationPreferences = baseUser.communicationPreferences || authUser.communicationPreferences || statsUser.communicationPreferences
    const subscriptionTierLabel = subscriptionTier || baseUser.subscription?.tier || authUser.subscription?.tier || statsUser.subscription?.tier || null
    const subscriptionStatus = subscription?.status || baseUser.subscription?.status || authUser.subscription?.status || statsUser.subscription?.status || null
    const autoRenew = subscription?.autoRenew ?? baseUser.subscription?.autoRenew ?? authUser.subscription?.autoRenew ?? statsUser.subscription?.autoRenew ?? null
    const subscriptionStart = subscription?.startDate || baseUser.subscription?.startDate || authUser.subscription?.startDate || statsUser.subscription?.startDate || null
    const gigUsageFormatted = formatLimitUsage(accessLimits?.gigs?.current ?? null, accessLimits?.gigs?.limit ?? null, accessLimits?.gigs?.unlimited)
    const applicationUsageFormatted = formatLimitUsage(accessLimits?.applications?.current ?? null, accessLimits?.applications?.limit ?? null, accessLimits?.applications?.unlimited)
    const gigResetSource = accessLimits?.gigs?.resetAt
    const applicationResetSource = accessLimits?.applications?.resetAt
    const gigReset = gigResetSource ? formatDate(gigResetSource) : '--'
    const applicationReset = applicationResetSource ? formatDate(applicationResetSource) : '--'
    const gigUnlimitedLabel = formatBooleanLabel(accessLimits?.gigs?.unlimited ?? null, 'Yes', 'No')
    const applicationUnlimitedLabel = formatBooleanLabel(accessLimits?.applications?.unlimited ?? null, 'Yes', 'No')
    const identityStatusLabel = formatLabelValue(identityStatus)
    const bankVerificationLabel = formatBooleanLabel(bankVerified ?? null, 'Verified', 'Pending')
    const twoFactorLabel = formatBooleanLabel(twoFactorEnabled ?? null)
    const emailNotificationsLabel = formatBooleanLabel(communicationPreferences?.emailNotifications ?? null)
    const smsNotificationsLabel = formatBooleanLabel(communicationPreferences?.smsNotifications ?? null)
    const pushNotificationsLabel = formatBooleanLabel(communicationPreferences?.pushNotifications ?? null)
    const emailSecurityAlertsLabel = formatBooleanLabel(communicationPreferences?.emailSecurityAlerts ?? null)
    const subscriptionTierDisplay = formatLabelValue(subscriptionTierLabel)
    const subscriptionStatusDisplay = formatLabelValue(subscriptionStatus)
    const autoRenewLabel = formatBooleanLabel(autoRenew, 'Enabled', 'Disabled')
    const subscriptionStartLabel = subscriptionStart ? formatDate(subscriptionStart) : '--'
    const trustLevelDisplay = formatLabelValue(trustSystemDetails?.level || null)
    const trustScoreDisplay = formatNumberValue(trustSystemDetails?.currentScore ?? null)
    const trustBaseScoreDisplay = formatNumberValue(trustSystemDetails?.baseScore ?? null)
    const completionRateDisplay = formatPercentValue(trustSystemDetails?.completionRate ?? null)
    const positiveReviewRateDisplay = formatPercentValue(trustSystemDetails?.positiveReviewRate ?? null)
    const disputeRateDisplay = formatPercentValue(trustSystemDetails?.disputeRate ?? null)
    const maxGigValueDisplay = trustSystemDetails?.accessLimits?.maxGigValue !== undefined && trustSystemDetails?.accessLimits?.maxGigValue !== null
        ? formatCurrency(trustSystemDetails.accessLimits.maxGigValue, (enrichedUser && 'earnings' in enrichedUser ? enrichedUser.earnings?.currency : undefined) || 'GHS')
        : '--'
    const maxApplicationValueDisplay = trustSystemDetails?.accessLimits?.maxApplicationValue !== undefined && trustSystemDetails?.accessLimits?.maxApplicationValue !== null
        ? formatCurrency(trustSystemDetails.accessLimits.maxApplicationValue, (enrichedUser && 'earnings' in enrichedUser ? enrichedUser.earnings?.currency : undefined) || 'GHS')
        : '--'
    const premiumCategoriesLabel = formatBooleanLabel(trustSystemDetails?.accessLimits?.canAccessPremiumCategories ?? null, 'Enabled', 'Restricted')
    const unlimitedPostingLabel = formatBooleanLabel(trustSystemDetails?.accessLimits?.canPostUnlimitedGigs ?? null, 'Enabled', 'Limited')
    const trustScoreUpdatedLabel = trustSystemDetails?.lastScoreUpdate ? formatDate(trustSystemDetails.lastScoreUpdate) : '--'
    const verificationBadgeLabel = formatBooleanLabel(verificationBenefits?.badgeEnabled ?? null, 'Enabled', 'Unavailable')
    const feeReductionLabel = verificationBenefits?.feeReduction !== undefined && verificationBenefits?.feeReduction !== null ? formatPercentValue(verificationBenefits.feeReduction) : '--'
    const premiumAccessLabel = formatBooleanLabel(verificationBenefits?.premiumAccess ?? null, 'Available', 'Locked')
    const prioritySupportLabel = formatBooleanLabel(verificationBenefits?.prioritySupport ?? null, 'Available', 'Standard')
    const higherLimitsLabel = formatBooleanLabel(verificationBenefits?.higherLimits ?? null, 'Enabled', 'Standard')

    const retryLoadAccessLimits = () => {
        refetchAccessLimits()
    }

    const handleRefreshData = async () => {
        setIsRefreshing(true)
        try {
            await Promise.allSettled([
                refetchDashboard(),
                refetchSubscription(),
                refetchAccessLimits(),
                refetchVerification(),
                successStoriesApi.getUserSuccessStory().then((res) => {
                    // @ts-expect-error - API returns nested response structure
                    if (res.data?.data?.story) {
                        // @ts-expect-error - API returns nested response structure
                        setSuccessStory(res.data.data.story)
                    }
                }),
            ])
        } catch (error) {
            console.error('Error refreshing dashboard data:', error)
        } finally {
            setIsRefreshing(false)
        }
    }

    return (
        <>
            <Helmet>
                <title>Dashboard - SideGO</title>
                <meta name="description" content="Your personal dashboard with insights and quick actions" />
            </Helmet>

            <div className="relative py-1 sm:py-2">
                <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-72 bg-gradient-to-b from-cyan-100/60 to-transparent dark:from-cyan-900/20" />
                {/* Page Header */}
                <div className="mb-4 overflow-hidden rounded-3xl border border-slate-200 bg-white/90 p-4 shadow-sm backdrop-blur dark:border-gray-800 dark:bg-gray-900/85 sm:mb-8 sm:p-6">
                    <div className="mb-4 flex flex-wrap items-center gap-2">
                        <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
                            {isWorker ? 'Worker Dashboard' : 'Employer Dashboard'}
                        </span>
                        <span className="inline-flex items-center rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-xs font-semibold text-cyan-700 dark:border-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300">
                            Live Activity
                        </span>
                    </div>
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-0">
                        <div>
                            <h1 className="mb-2 text-2xl font-bold text-slate-900 dark:text-white sm:text-3xl">
                                Welcome back, {dashboardUser?.profile?.firstName || user?.profile?.firstName || 'there'}! 👋
                            </h1>
                            <p className="text-sm text-slate-600 dark:text-gray-400 sm:text-base">
                                {isWorker
                                    ? "Here's an overview of your work activity"
                                    : "Here's an overview of your hiring activity"
                                }
                            </p>
                        </div>
                        <button
                            onClick={handleRefreshData}
                            disabled={isRefreshing || dashboardLoading}
                            className="inline-flex items-center gap-2 self-start rounded-xl bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-gray-400 sm:self-auto"
                            title="Refresh all data"
                        >
                            <FiRefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                            <span className="hidden sm:inline">Refresh</span>
                        </button>
                    </div>
                </div>

                {/* Verification Status & Access Limits */}
                {user && !accessLimitsLoading && (
                    <div className="mb-6 sm:mb-8">
                        {/* Email Verification Banner */}
                        <EmailVerificationBanner
                            user={user}
                            onVerifyClick={() => navigate('/verify-email', { state: { email: user?.email } })}
                            className="mb-4 sm:mb-6"
                        />

                        {/* Error loading access limits */}
                        {accessLimitsError && (
                            <div className="mb-6 flex items-center justify-between rounded-2xl border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
                                <div className="flex items-center gap-3">
                                    <FiAlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                                    <p className="text-sm text-red-800 dark:text-red-200">
                                        {'status' in accessLimitsError 
                                            ? `Error loading access limits (${accessLimitsError.status})`
                                            : 'message' in accessLimitsError 
                                            ? accessLimitsError.message 
                                            : 'Failed to load access limits. Please refresh.'}
                                    </p>
                                </div>
                                <button
                                    onClick={retryLoadAccessLimits}
                                    className="rounded-lg border border-red-300 px-3 py-1 text-sm font-medium text-red-600 transition-colors hover:bg-red-100 hover:text-red-700 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900/30 dark:hover:text-red-300"
                                >
                                    Retry
                                </button>
                            </div>
                        )}

                        {/* Subscription upgrade banner (verification independent) */}
                        {shouldShowUpgradeBanner && (
                            <VerificationBanner
                                user={user}
                                type="general"
                                variant="full"
                                className="mb-6"
                                verificationStatus={overallVerificationStatus as any}
                            />
                        )}

                        {/* Access Limits Summary */}
                        {accessLimits && (
                            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900 sm:p-6">
                                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-3 sm:gap-0">
                                    <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                        <FiShield className="w-5 h-5 text-primary-600 flex-shrink-0" />
                                        Access Limits & Usage
                                    </h2>
                                    {!hasUnlimitedAccess && (
                                        <Link
                                            to="/subscription"
                                            className="flex items-center gap-1 text-primary-600 hover:text-primary-700 text-sm font-medium whitespace-nowrap"
                                        >
                                            Upgrade Plan
                                            <FiArrowRight className="w-4 h-4" />
                                        </Link>
                                    )}
                                </div>

                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-2">
                                    {/* Gig Creation Limits (for employers) */}
                                    {user.role === 'employer' && (
                                        <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 dark:border-blue-900 dark:bg-blue-900/20 sm:p-4">
                                            <div className="flex items-center justify-between mb-4">
                                                <div>
                                                    <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                                                        Gig Posts
                                                    </p>
                                                    <p className="text-xl sm:text-2xl font-bold text-blue-700 dark:text-blue-300">
                                                        {gigUsage?.unlimited ? '∞' :
                                                            `${accessLimits.gigs.current}/${accessLimits.gigs.limit}`
                                                        }
                                                    </p>
                                                </div>
                                                <div className="text-blue-600 dark:text-blue-400">
                                                    {gigUsage?.unlimited ? (
                                                        <FiCheckCircle className="w-7 h-7 sm:w-8 sm:h-8" />
                                                    ) : (
                                                        <FiBriefcase className="w-7 h-7 sm:w-8 sm:h-8" />
                                                    )}
                                                </div>
                                            </div>
                                            <ProgressIndicator
                                                current={accessLimits.gigs.current}
                                                max={accessLimits.gigs.limit}
                                                type="gigs"
                                                unlimited={Boolean(gigUsage?.unlimited)}
                                            />
                                        </div>
                                    )}

                                    {/* Application Limits (for workers) */}
                                    {user.role === 'worker' && (
                                        <div className="rounded-xl border border-green-200 bg-green-50 p-3 dark:border-green-900 dark:bg-green-900/20 sm:p-4">
                                            <div className="flex items-center justify-between mb-4">
                                                <div>
                                                    <p className="text-sm font-medium text-green-900 dark:text-green-100">
                                                        Job Applications
                                                    </p>
                                                    <p className="text-xl sm:text-2xl font-bold text-green-700 dark:text-green-300">
                                                        {applicationUsage?.unlimited ? '∞' :
                                                            `${accessLimits.applications.current}/${accessLimits.applications.limit}`
                                                        }
                                                    </p>
                                                </div>
                                                <div className="text-green-600 dark:text-green-400">
                                                    {applicationUsage?.unlimited ? (
                                                        <FiCheckCircle className="w-7 h-7 sm:w-8 sm:h-8" />
                                                    ) : (
                                                        <FiUsers className="w-7 h-7 sm:w-8 sm:h-8" />
                                                    )}
                                                </div>
                                            </div>
                                            <ProgressIndicator
                                                current={accessLimits.applications.current}
                                                max={accessLimits.applications.limit}
                                                type="applications"
                                                unlimited={Boolean(applicationUsage?.unlimited)}
                                            />
                                        </div>
                                    )}

                                    {/* Verification Status */}
                                    <div className="flex items-center justify-between rounded-xl border border-purple-200 bg-purple-50 p-3 dark:border-purple-900 dark:bg-purple-900/20 sm:p-4">
                                        <div>
                                            <p className="text-sm font-medium text-purple-900 dark:text-purple-100">
                                                Verification Status
                                            </p>
                                            <p className="text-base sm:text-lg font-bold text-purple-700 dark:text-purple-300">
                                                {overallVerificationStatus === 'verified' ? 'Verified' : 'Unverified'}
                                            </p>
                                            <p className="text-xs sm:text-sm text-purple-600 dark:text-purple-400">
                                                {
                                                    (() => {
                                                        const isUnlimited = Boolean(hasUnlimitedAccess)

                                                        if (isUnlimited) {
                                                            return 'Unlimited access enabled'
                                                        }
                                                        return overallVerificationStatus === 'verified'
                                                            ? 'Limited access - upgrade for unlimited'
                                                            : 'Limited access - verify to unlock'
                                                    })()
                                                }
                                            </p>
                                        </div>
                                        <div className="text-purple-600 dark:text-purple-400 flex-shrink-0">
                                            {
                                                (() => {
                                                    const isUnlimited = Boolean(hasUnlimitedAccess)

                                                    if (isUnlimited) {
                                                        return <FiCheckCircle className="w-7 h-7 sm:w-8 sm:h-8" />
                                                    }
                                                    return <FiAlertCircle className="w-7 h-7 sm:w-8 sm:h-8" />
                                                })()
                                            }
                                        </div>
                                    </div>
                                </div>

                                {/* Upgrade CTA for users who have reached their limit */}
                                {isLimitReached && (
                                    <div className="mt-4 rounded-xl border border-primary-200 bg-primary-50 p-3 dark:border-primary-800 dark:bg-primary-900/20 sm:mt-6 sm:p-4">
                                        <div className="flex items-start gap-3">
                                            <FiTrendingUp className="w-5 h-5 text-primary-600 dark:text-primary-400 mt-0.5 flex-shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <h3 className="text-sm font-semibold text-primary-900 dark:text-primary-100">
                                                    Upgrade Your Plan for Unlimited Access
                                                </h3>
                                                <p className="text-xs sm:text-sm text-primary-700 dark:text-primary-300 mt-1">
                                                    {user.role === 'employer'
                                                        ? 'Upgrade to Pro or Enterprise to post unlimited gigs and unlock premium employer features'
                                                        : 'Upgrade to Pro or Enterprise to apply to unlimited jobs and unlock priority worker benefits'
                                                    }
                                                </p>
                                                <Link
                                                    to="/subscription"
                                                    className="inline-flex items-center gap-1 mt-2 px-3 py-1 bg-primary-600 text-white text-xs font-medium rounded hover:bg-primary-700 transition-colors"
                                                >
                                                    View Plans
                                                    <FiArrowRight className="w-3 h-3" />
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Verification Progress Section */}
                        {user && overallVerificationStatus !== 'verified' && (
                            <div className="mt-6">
                                <VerificationProgress user={user} />
                            </div>
                        )}
                    </div>
                )}

                {/* Subscription Upgrade Banner */}
                {!subscriptionLoading && ((!subscription || subscriptionTier === 'free' || subscription?.monthlyAmount === 0 || subscription?.monthlyAmount === undefined) || isLimitReached) && (
                    <div className="mb-6 rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 p-3.5 dark:border-amber-800 dark:from-amber-900/30 dark:to-orange-900/30 sm:mb-8 sm:p-6">
                        <div className="min-w-0">
                            <h3 className="mb-1 flex items-center gap-2 text-base font-semibold text-amber-900 dark:text-amber-100 sm:text-lg">
                                <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-amber-100 dark:bg-amber-900/40">
                                    <FiTrendingDown className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                                </span>
                                <span>Upgrade Your Plan</span>
                            </h3>
                                <p className="mb-3 text-sm leading-relaxed text-amber-800 dark:text-amber-200 sm:mb-4">
                                    {isLimitReached && (!subscription || subscriptionTier === 'free' || subscription?.monthlyAmount === 0 || subscription?.monthlyAmount === undefined)
                                        ? 'You have reached your posting limit. Upgrade to Pro or Enterprise to continue posting and unlock unlimited access.'
                                        : isLimitReached
                                            ? 'You have reached your posting limit for this month. Upgrade your plan for higher limits or unlimited access.'
                                            : 'You\'re currently on the free plan. Upgrade to Pro or Enterprise to unlock premium features, higher limits, and priority support.'
                                    }
                                </p>
                                <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
                                    <Link
                                        to="/subscription"
                                        className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-amber-700 sm:w-auto sm:min-w-[140px]"
                                    >
                                        View Plans
                                        <FiArrowRight className="w-4 h-4" />
                                    </Link>
                                    <button
                                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                                        className="inline-flex w-full items-center justify-center rounded-lg border border-amber-200 bg-white px-4 py-2 text-sm font-medium text-amber-600 transition-colors hover:bg-amber-50 dark:border-amber-800 dark:bg-gray-800 dark:text-amber-400 dark:hover:bg-gray-700 sm:w-auto sm:min-w-[140px]"
                                    >
                                        Dismiss
                                    </button>
                                </div>
                        </div>
                    </div>
                )}

                {/* Stats Grid */}
                <div className="mb-6 grid grid-cols-2 gap-3 sm:mb-8 sm:grid-cols-2 sm:gap-5 lg:grid-cols-4">
                    <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg dark:border-gray-800 dark:bg-gray-900 sm:p-5">
                        <div className="flex items-center justify-between">
                            <div className="min-w-0 flex-1">
                                <p className="text-[11px] leading-tight sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                                    {isWorker ? 'Applications Sent' : 'Posted Gigs'}
                                </p>
                                <p className="mt-0.5 text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                                    {stats.totalGigs}
                                </p>
                            </div>
                            <div className="rounded-full bg-blue-100 p-2 dark:bg-blue-900 sm:p-3 flex-shrink-0">
                                <FiBriefcase className="w-4 h-4 sm:w-6 sm:h-6 text-blue-600 dark:text-blue-400" />
                            </div>
                        </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg dark:border-gray-800 dark:bg-gray-900 sm:p-5">
                        <div className="flex items-center justify-between">
                            <div className="min-w-0 flex-1">
                                <p className="text-[11px] leading-tight sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                                    {isWorker ? 'Active Applications' : 'Total Applications'}
                                </p>
                                <p className="mt-0.5 text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                                    {stats.activeApplications}
                                </p>
                            </div>
                            <div className="rounded-full bg-yellow-100 p-2 dark:bg-yellow-900 sm:p-3 flex-shrink-0">
                                <FiUsers className="w-4 h-4 sm:w-6 sm:h-6 text-yellow-600 dark:text-yellow-400" />
                            </div>
                        </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg dark:border-gray-800 dark:bg-gray-900 sm:p-5">
                        <div className="flex items-center justify-between">
                            <div className="min-w-0 flex-1">
                                <p className="text-[11px] leading-tight sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                                    {isWorker ? 'Total Earnings' : 'Total Spent'}
                                </p>
                                <p className="mt-0.5 text-lg sm:text-2xl font-bold text-gray-900 dark:text-white truncate">
                                    {formatCurrency(stats.earnings, 'GHS')}
                                </p>
                            </div>
                            <div className="rounded-full bg-green-100 p-2 dark:bg-green-900 sm:p-3 flex-shrink-0">
                                <FiDollarSign className="w-4 h-4 sm:w-6 sm:h-6 text-green-600 dark:text-green-400" />
                            </div>
                        </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg dark:border-gray-800 dark:bg-gray-900 sm:p-5">
                        <div className="flex items-center justify-between">
                            <div className="min-w-0 flex-1">
                                <p className="text-[11px] leading-tight sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                                    Average Rating
                                </p>
                                <div className="mt-0.5 flex items-center gap-1.5 sm:gap-2">
                                    <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                                        {stats.rating}
                                    </p>
                                    <FiStar className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-yellow-500 fill-current flex-shrink-0" />
                                </div>
                            </div>
                            <div className="rounded-full bg-purple-100 p-2 dark:bg-purple-900 sm:p-3 flex-shrink-0">
                                <FiStar className="w-4 h-4 sm:w-6 sm:h-6 text-purple-600 dark:text-purple-400" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Wallet Widget */}
                <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm dark:border-gray-800 dark:bg-gray-900 sm:mb-8 sm:p-5">
                    <div className="mb-3 flex items-center justify-between sm:mb-4">
                        <h2 className="text-base font-bold text-gray-900 dark:text-white sm:text-xl">
                            Wallet Overview
                        </h2>
                        <Link
                            to="/dashboard/wallet"
                            className="text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 sm:text-sm"
                        >
                            View Details →
                        </Link>
                    </div>
                    <BalanceCard
                        balance={walletBalance}
                        loading={false}
                        onRefresh={() => dispatch(fetchBalance(undefined))}
                        showActions={true}
                        onDeposit={() => navigate('/dashboard/wallet')}
                        onWithdraw={() => navigate('/dashboard/wallet')}
                        onTransfer={() => navigate('/dashboard/wallet')}
                    />
                </div>

                <Accordion className="mb-6 sm:mb-8">
                    <AccordionItem 
                        title="Access Snapshot" 
                        defaultOpen={true}
                        icon={<FiShield className="w-5 h-5" />}
                    >
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Gig Posts</span>
                                <span className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">{gigUsageFormatted}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Job Applications</span>
                                <span className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">{applicationUsageFormatted}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Next Reset (Gigs)</span>
                                <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">{gigReset}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Next Reset (Applications)</span>
                                <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">{applicationReset}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Unlimited Gig Posts</span>
                                <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">{gigUnlimitedLabel}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Unlimited Applications</span>
                                <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">{applicationUnlimitedLabel}</span>
                            </div>
                        </div>
                    </AccordionItem>

                    <AccordionItem 
                        title="Trust & Safety" 
                        icon={<FiAward className="w-5 h-5" />}
                    >
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Trust Level</span>
                                <span className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">{trustLevelDisplay}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Trust Score</span>
                                <span className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">{trustScoreDisplay}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Base Score</span>
                                <span className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">{trustBaseScoreDisplay}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Completion Rate</span>
                                <span className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">{completionRateDisplay}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Positive Review Rate</span>
                                <span className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">{positiveReviewRateDisplay}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Dispute Rate</span>
                                <span className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">{disputeRateDisplay}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Max Gig Value</span>
                                <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">{maxGigValueDisplay}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Max Application Value</span>
                                <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">{maxApplicationValueDisplay}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Premium Categories</span>
                                <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">{premiumCategoriesLabel}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Unlimited Posting</span>
                                <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">{unlimitedPostingLabel}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Score Updated</span>
                                <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">{trustScoreUpdatedLabel}</span>
                            </div>
                        </div>
                    </AccordionItem>

                    <AccordionItem 
                        title="Subscription Snapshot" 
                        icon={<FiDollarSign className="w-5 h-5" />}
                    >
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Subscription Tier</span>
                                <span className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">{subscriptionTierDisplay}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Subscription Status</span>
                                <span className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">{subscriptionStatusDisplay}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Auto Renew</span>
                                <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">{autoRenewLabel}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Start Date</span>
                                <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">{subscriptionStartLabel}</span>
                            </div>
                        </div>
                        <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-4 space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Verification Badge</span>
                                <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">{verificationBadgeLabel}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Fee Reduction</span>
                                <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">{feeReductionLabel}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Premium Access</span>
                                <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">{premiumAccessLabel}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Priority Support</span>
                                <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">{prioritySupportLabel}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Higher Limits</span>
                                <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">{higherLimitsLabel}</span>
                            </div>
                        </div>
                    </AccordionItem>

                    <AccordionItem 
                        title="Identity & Alerts" 
                        icon={<FiLock className="w-5 h-5" />}
                    >
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Identity Status</span>
                                <span className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">{identityStatusLabel}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Bank Details</span>
                                <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">{bankVerificationLabel}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Two-Factor</span>
                                <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">{twoFactorLabel}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Email Notifications</span>
                                <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">{emailNotificationsLabel}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Email Security Alerts</span>
                                <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">{emailSecurityAlertsLabel}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">SMS Notifications</span>
                                <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">{smsNotificationsLabel}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Push Notifications</span>
                                <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">{pushNotificationsLabel}</span>
                            </div>
                        </div>
                    </AccordionItem>
                </Accordion>

                {/* Success Story Section */}
                {!storyLoading && (
                    <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900 sm:mb-8 sm:p-6">
                        <div className="flex items-center justify-between mb-4 sm:mb-6">
                            <h2 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                <FiBook className="w-5 h-5 text-blue-600 flex-shrink-0" />
                                Success Story
                            </h2>
                        </div>

                        {successStory ? (
                            <div className="space-y-3 sm:space-y-4">
                                <div>
                                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-1">
                                        Title
                                    </p>
                                    <p className="text-base sm:text-lg font-medium text-gray-900 dark:text-white line-clamp-2">
                                        {successStory.title}
                                    </p>
                                </div>

                                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
                                    <div className="flex-1 w-full sm:w-auto">
                                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-1">
                                            Status
                                        </p>
                                        <div className="flex items-center">
                                            {successStory.status === 'pending' && (
                                                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 text-xs sm:text-sm font-medium rounded-full">
                                                    <FiClock className="w-3 h-3" />
                                                    Pending Review
                                                </span>
                                            )}
                                            {successStory.status === 'approved' && (
                                                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 text-xs sm:text-sm font-medium rounded-full">
                                                    <FiCheckCircle className="w-3 h-3" />
                                                    Published
                                                </span>
                                            )}
                                            {successStory.status === 'rejected' && (
                                                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 text-xs sm:text-sm font-medium rounded-full">
                                                    <FiAlertCircle className="w-3 h-3" />
                                                    Rejected
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {successStory.status === 'approved' && (
                                        <div className="flex-1 w-full sm:w-auto">
                                            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-1">
                                                Views
                                            </p>
                                            <p className="text-base sm:text-lg font-medium text-gray-900 dark:text-white">
                                                {successStory.views}
                                            </p>
                                        </div>
                                    )}
                                </div>

                                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-4 sm:mt-6">
                                    <button
                                        onClick={() => navigate('/dashboard/success-story')}
                                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
                                    >
                                        <FiExternalLink className="w-4 h-4" />
                                        View Full Story
                                    </button>

                                    {successStory.status !== 'archived' && (
                                        <button
                                            onClick={() => navigate(`/success-stories/${successStory._id}/edit`)}
                                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
                                        >
                                            <FiEdit2 className="w-4 h-4" />
                                            Edit
                                        </button>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-6 sm:py-8">
                                <FiBook className="w-10 h-10 sm:w-12 sm:h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3 sm:mb-4" />
                                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-4 sm:mb-6 px-2">
                                    You haven't submitted a success story yet. Share your journey and inspire others!
                                </p>
                                <button
                                    onClick={() => navigate('/success-stories/submit')}
                                    className="px-4 sm:px-6 py-2 text-sm sm:text-base bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
                                >
                                    Submit Your Story
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Quick Actions & Recent Activity */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 mb-6 sm:mb-8">
                    {/* Quick Actions Card */}
                    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900 sm:p-6">
                        <h2 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-900 dark:text-white mb-4 sm:mb-6">
                            Quick Actions
                        </h2>
                        <div className="grid grid-cols-2 gap-3 sm:gap-4">
                            {isWorker ? (
                                <>
                                    <Link
                                        to="/gigs"
                                        className="flex flex-col items-center rounded-xl border border-blue-100 bg-blue-50 p-3 transition-colors hover:bg-blue-100 dark:border-blue-900 dark:bg-blue-900/20 dark:hover:bg-blue-900/30 sm:p-4"
                                    >
                                        <FiSearch className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600 dark:text-blue-400 mb-2" />
                                        <span className="text-xs sm:text-sm font-medium text-blue-700 dark:text-blue-300 text-center">
                                            Browse Gigs
                                        </span>
                                    </Link>
                                    <Link
                                        to="/dashboard/applications"
                                        className="flex flex-col items-center rounded-xl border border-green-100 bg-green-50 p-3 transition-colors hover:bg-green-100 dark:border-green-900 dark:bg-green-900/20 dark:hover:bg-green-900/30 sm:p-4"
                                    >
                                        <FiBriefcase className="w-6 h-6 sm:w-8 sm:h-8 text-green-600 dark:text-green-400 mb-2" />
                                        <span className="text-xs sm:text-sm font-medium text-green-700 dark:text-green-300 text-center">
                                            My Applications
                                        </span>
                                    </Link>
                                </>
                            ) : (
                                <>
                                    <Link
                                        to="/dashboard/gigs/create"
                                        className="flex flex-col items-center rounded-xl border border-blue-100 bg-blue-50 p-3 transition-colors hover:bg-blue-100 dark:border-blue-900 dark:bg-blue-900/20 dark:hover:bg-blue-900/30 sm:p-4"
                                    >
                                        <FiPlus className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600 dark:text-blue-400 mb-2" />
                                        <span className="text-xs sm:text-sm font-medium text-blue-700 dark:text-blue-300 text-center">
                                            Post New Gig
                                        </span>
                                    </Link>
                                    <Link
                                        to="/dashboard/applications"
                                        className="flex flex-col items-center rounded-xl border border-green-100 bg-green-50 p-3 transition-colors hover:bg-green-100 dark:border-green-900 dark:bg-green-900/20 dark:hover:bg-green-900/30 sm:p-4"
                                    >
                                        <FiUsers className="w-6 h-6 sm:w-8 sm:h-8 text-green-600 dark:text-green-400 mb-2" />
                                        <span className="text-xs sm:text-sm font-medium text-green-700 dark:text-green-300 text-center">
                                            View Applications
                                        </span>
                                    </Link>
                                </>
                            )}
                            <Link
                                to="/dashboard/messages"
                                className="flex flex-col items-center rounded-xl border border-purple-100 bg-purple-50 p-3 transition-colors hover:bg-purple-100 dark:border-purple-900 dark:bg-purple-900/20 dark:hover:bg-purple-900/30 sm:p-4"
                            >
                                <FiMessageSquare className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600 dark:text-purple-400 mb-2" />
                                <span className="text-xs sm:text-sm font-medium text-purple-700 dark:text-purple-300 text-center">
                                    Messages
                                </span>
                            </Link>
                            <Link
                                to="/dashboard/profile/edit"
                                className="flex flex-col items-center rounded-xl border border-yellow-100 bg-yellow-50 p-3 transition-colors hover:bg-yellow-100 dark:border-yellow-900 dark:bg-yellow-900/20 dark:hover:bg-yellow-900/30 sm:p-4"
                            >
                                <FiUsers className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-600 dark:text-yellow-400 mb-2" />
                                <span className="text-xs sm:text-sm font-medium text-yellow-700 dark:text-yellow-300 text-center">
                                    Edit Profile
                                </span>
                            </Link>
                        </div>
                    </div>

                    {/* Recent Activity */}
                    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900 sm:p-6">
                        <h2 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-900 dark:text-white mb-4 sm:mb-6">
                            Recent Activity
                        </h2>
                        {recentActivity.length > 0 ? (
                            <div className="space-y-3 sm:space-y-4">
                                {recentActivity.map((activity) => (
                                    <div
                                        key={activity.id}
                                        className="flex items-start gap-2 rounded-xl border border-slate-200 bg-slate-50 p-2.5 dark:border-gray-700 dark:bg-gray-800 sm:gap-3 sm:p-3"
                                    >
                                        <div className="flex-shrink-0">
                                            {activity.type === 'application' && (
                                                <div className={`p-1.5 sm:p-2 rounded-full ${activity.status === 'accepted'
                                                    ? 'bg-green-100 dark:bg-green-900'
                                                    : 'bg-blue-100 dark:bg-blue-900'
                                                    }`}>
                                                    <FiBriefcase className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${activity.status === 'accepted'
                                                        ? 'text-green-600 dark:text-green-400'
                                                        : 'text-blue-600 dark:text-blue-400'
                                                        }`} />
                                                </div>
                                            )}
                                            {activity.type === 'message' && (
                                                <div className="p-1.5 sm:p-2 bg-purple-100 dark:bg-purple-900 rounded-full">
                                                    <FiMessageSquare className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-purple-600 dark:text-purple-400" />
                                                </div>
                                            )}
                                            {activity.type === 'rating_received' && (
                                                <div className="p-1.5 sm:p-2 bg-yellow-100 dark:bg-yellow-900 rounded-full">
                                                    <FiStar className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-yellow-600 dark:text-yellow-400" />
                                                </div>
                                            )}
                                            {activity.type === 'gig_completed' && (
                                                <div className="p-1.5 sm:p-2 bg-green-100 dark:bg-green-900 rounded-full">
                                                    <FiCheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-600 dark:text-green-400" />
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                                                {activity.title}
                                            </p>
                                            <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 sm:mt-1 line-clamp-2">
                                                {activity.description}
                                            </p>
                                            <p className="text-xs text-gray-500 dark:text-gray-500 mt-0.5 sm:mt-1">
                                                {formatDate(activity.date)}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-6 sm:py-8">
                                <FiClock className="w-10 h-10 sm:w-12 sm:h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3 sm:mb-4" />
                                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                                    No recent activity yet
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Additional Stats */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
                    {/* Performance Chart Placeholder */}
                    <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900 sm:p-6">
                        <h2 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-900 dark:text-white mb-4 sm:mb-6">
                            {isWorker ? 'Earnings Overview' : 'Hiring Activity'}
                        </h2>
                        <div className="flex h-48 items-center justify-center rounded-xl bg-slate-50 dark:bg-gray-800 sm:h-64">
                            <div className="text-center px-4">
                                <FiTrendingUp className="w-10 h-10 sm:w-12 sm:h-12 text-gray-300 dark:text-gray-600 mx-auto mb-2 sm:mb-4" />
                                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                                    Performance chart coming soon
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Profile Summary */}
                    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900 sm:p-6">
                        <h2 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-900 dark:text-white mb-4 sm:mb-6">
                            Profile Summary
                        </h2>
                        <div className="space-y-3 sm:space-y-4">
                            <div className="flex items-center justify-between">
                                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                                    Profile Views
                                </span>
                                <div className="flex items-center gap-1">
                                    <FiEye className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-500" />
                                    <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                                        {stats.profileViews}
                                    </span>
                                </div>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                                    Completed Jobs
                                </span>
                                <div className="flex items-center gap-1">
                                    <FiCheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-500" />
                                    <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                                        {stats.completedJobs}
                                    </span>
                                </div>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                                    Member Since
                                </span>
                                <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                                    {dashboardUser?.createdAt ? formatDate(dashboardUser.createdAt) :
                                        user?.createdAt ? formatDate(user.createdAt) : 'Recently'}
                                </span>
                            </div>

                            <Link
                                to="/dashboard/profile"
                                className="w-full mt-2 sm:mt-4 px-4 py-2 bg-primary-600 text-white text-xs sm:text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors text-center block"
                            >
                                View Full Profile
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

        </>
    )
}

export default DashboardPage

// end of DashboardPage.tsx